import React, { useCallback, useEffect, useState } from "react";
import "./index.less";
import { useTranslate } from "@obsbot/remo-i18n";
import { Button, Form } from "antd";
import { useSafePromise } from "../../../utils/hooks";
import { LoginAgreement, LoginAgreementProps } from "../LoginAgreement";
import { LoginPanelContent } from "../LoginPanelContent";
// import { requestAgreement } from "../LoginAgreement";
import { LoginAccount } from "../LoginAccount";
import { LoginButtonProviderType } from "../LoginButton";
import { LoginButtonsProps } from "../LoginButtons";
import { accountValidator } from "./validators";
import { codeValidator } from "../../../utils/validators";
import { LoginSendCode } from "../LoginSendCode";
import { RememberPassword } from "@obsbot/remo-utils";

interface CodeLoginFormValues {
    account: string;
    code: string;
}

export interface LoginWithCodeProps {
    buttons?: LoginButtonProviderType[];
    privacyURL?: LoginAgreementProps["privacyURL"];
    serviceURL?: LoginAgreementProps["serviceURL"];
    login: (account: string, code: string) => Promise<boolean>;
    onClickButton?: LoginButtonsProps["onClick"];
    sendVerificationCode: (account: string) => Promise<boolean>;
}

export const LoginWithCode: React.FC<LoginWithCodeProps> = ({
    privacyURL,
    serviceURL,
    login,
    sendVerificationCode,
}) => {
    const t = useTranslate();
    const sp = useSafePromise();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [agreed, setAgreed] = useState(false);
    const [clickedLogin, setClickedLogin] = useState(false);
    const [form] = Form.useForm<CodeLoginFormValues>();
    const [isFormValidated, setIsFormValidated] = useState(false);
    const [isAccountValidated, setIsAccountValidated] = useState(false);

    const defaultValues = {
        account: "",
        code: "",
    };
    // const [password, setPassword] = useState();

    useEffect(() => {
        const cryptoKey = window.isElectron
            ? (window as any).node.os.hostname()
            : navigator.userAgent;
        const password = RememberPassword.getPassword(cryptoKey);
        if (password) {
            const info = JSON.parse(password);
            form.setFieldValue("account", info.account);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const doLogin = useCallback(async () => {
        // TODO: 这里是因为测试submenu是否有效所以注释了, 后续按需启用
        // if (!agreed) {
        //     if (!(await requestAgreement({ t, privacyURL, serviceURL }))) {
        //         return;
        //     }
        //     setAgreed(true);
        // }
        setClickedLogin(true);

        const { account: accountValue, code } = form.getFieldsValue();
        sp(login(accountValue, code))
            .then(res => {
                if (res) {
                    const cryptoKey = window.isElectron
                        ? (window as any).node.os.hostname()
                        : navigator.userAgent;
                    // 登录成功保存账号
                    RememberPassword.savePassword(
                        { account: accountValue, password: "" },
                        cryptoKey,
                        7,
                    );
                    console.info(t("login-page.login-success"));
                } else {
                    console.info(t("login-page.login-failed"));
                }
                setClickedLogin(false);
            })
            .catch(error => {
                console.error(error);
                setClickedLogin(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);

    const handleSendVerificationCode = async (): Promise<boolean> => {
        const { account: accountValue } = form.getFieldsValue();

        return sendVerificationCode(accountValue);
    };

    const formValidateStatus = useCallback(() => {
        const condition =
            form.getFieldsError().every(field => field.errors.length <= 0) &&
            !!form.getFieldValue("account") &&
            !!form.getFieldValue("code");

        setIsFormValidated(condition);

        if (form.getFieldValue("account") && !form.getFieldError("account").length) {
            setIsAccountValidated(true);
        } else {
            setIsAccountValidated(false);
        }
    }, [form]);

    useEffect(() => {
        formValidateStatus();
    }, [form, formValidateStatus]);

    function renderLoginPage(): React.ReactNode {
        return (
            <div className="login-with-code">
                <div className="login-width-limiter">
                    <Form
                        form={form}
                        initialValues={defaultValues}
                        name="LoginWithCode"
                        onFieldsChange={formValidateStatus}
                        onFinish={doLogin}
                    >
                        <Form.Item
                            name="account"
                            rules={[accountValidator(t)]}
                            validateTrigger={["onBlur"]}
                        >
                            <LoginAccount
                                placeholder={t("login-page.enter-email-or-phone")}
                                onFocus={() => form.setFields([{ name: "account", errors: [] }])}
                            />
                        </Form.Item>

                        <Form.Item name="code" rules={[codeValidator]}>
                            <LoginSendCode
                                isAccountValidated={isAccountValidated}
                                sendVerificationCode={handleSendVerificationCode}
                                onFocus={() => form.setFields([{ name: "code", errors: [] }])}
                            />
                        </Form.Item>

                        <div className="login-with-email-btn-wrapper"></div>
                        <Button
                            className="login-big-button login-with-email-login-btn"
                            disabled={!isFormValidated}
                            htmlType="submit"
                            loading={clickedLogin}
                            type="primary"
                            // onClick={doLogin}
                        >
                            {t("login-page.login")}
                        </Button>
                    </Form>

                    <LoginAgreement privacyURL={privacyURL} serviceURL={serviceURL} />
                </div>
            </div>
        );
    }
    return <LoginPanelContent transitionKey={"login"}>{renderLoginPage()}</LoginPanelContent>;
};
